/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Focused Practice Review Page - Pre-practice review of knowledge and wrong questions (UC-111, UC-112)
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import useFocusedPracticeStore from '../stores/focusedPracticeStore'
import {
  Target,
  BookOpen,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  PlayCircle,
  Eye,
  RefreshCw,
  BarChart3
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import SkillContentTabs from '../components/SkillContentTabs'
import { renderContent } from '../utils/contentRenderer'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/errorUtils.js'
import api from '../services/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('FocusedPracticeReview')

const FocusedPracticeReview = () => {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const {
    currentSession,
    prePracticeContent,
    loading,
    error,
    getSession,
    getPrePracticeContent,
    completePrePracticeReview,
    startPractice,
    resetSession
  } = useFocusedPracticeStore()

  const [expandedQuestions, setExpandedQuestions] = useState({})
  const [expandedSkills, setExpandedSkills] = useState({})
  const [reviewedIds, setReviewedIds] = useState([])
  const [isStarting, setIsStarting] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [viewMode, setViewMode] = useState('accordion') // 'accordion' or 'wizard'
  const [activeSkillIndex, setActiveSkillIndex] = useState(0)
  const [skillGroups, setSkillGroups] = useState([])

  // Practice completion state
  const [practiceStatus, setPracticeStatus] = useState({
    isStarted: false,
    isCompleted: false,
    answeredCount: 0,
    totalQuestions: 0,
    challengeId: null
  })
  // Tutor-assigned homework: taken on a dedicated page that reveals each answer right after it's
  // answered, instead of the standard (review-at-end) Challenge session.
  const [isHomework, setIsHomework] = useState(false)

  useEffect(() => {
    loadContent()
  }, [sessionId])

  const loadContent = async () => {
    const sessionResult = await getSession(sessionId)
    if (!sessionResult.success) {
      toast.error(getErrorMessage(sessionResult.error))
      navigate('/focused-practice')
      return
    }

    const status = sessionResult.data.status
    const challengeId = sessionResult.data.challengeId
    setIsHomework(!!sessionResult.data.revealPerQuestion)

    // Check practice completion status for in_progress or completed sessions
    if (['in_progress', 'completed'].includes(status) && challengeId) {
      await checkPracticeCompletion(challengeId, status)
    }

    // Fetch pre-practice content for all states (educational content + wrong questions)
    // This content is useful for review regardless of practice status
    const contentResult = await getPrePracticeContent(sessionId)
    if (!contentResult.success && ['pending', 'pre_practice'].includes(status)) {
      // Only show error for states where this content is essential
      toast.error(getErrorMessage(contentResult.error))
    }
  }

  // Check how many practice questions have been answered
  const checkPracticeCompletion = async (challengeId, sessionStatus) => {
    try {
      // Get challenge to find stage info
      const challengeResult = await api.get(`/challenges/${challengeId}`)
      if (!challengeResult.data?.success || !challengeResult.data?.data?.stages?.length) {
        setPracticeStatus({
          isStarted: true,
          isCompleted: sessionStatus === 'completed',
          answeredCount: 0,
          totalQuestions: 0,
          challengeId
        })
        return
      }

      const stage = challengeResult.data.data.stages[0]
      const totalQuestions = stage.questions?.length || 0

      // Get responses
      const responsesResult = await api.get(
        `/challenges/${challengeId}/stages/${stage.id}/responses`
      )
      const answeredCount = responsesResult.data?.data?.length || 0
      const isAllAnswered = answeredCount >= totalQuestions && totalQuestions > 0

      setPracticeStatus({
        isStarted: true,
        isCompleted: sessionStatus === 'completed' || isAllAnswered,
        answeredCount,
        totalQuestions,
        challengeId
      })
    } catch (err) {
      logger.error('Failed to check practice completion:', err)
      setPracticeStatus({
        isStarted: true,
        isCompleted: sessionStatus === 'completed',
        answeredCount: 0,
        totalQuestions: 0,
        challengeId
      })
    }
  }

  // Restructure data by skill when prePracticeContent loads
  useEffect(() => {
    if (prePracticeContent) {
      // Group wrong questions by skill
      const questionsBySkill = {}
      prePracticeContent.wrongQuestions?.forEach((q) => {
        const skillName = q.skill || 'Unknown Skill'
        if (!questionsBySkill[skillName]) {
          questionsBySkill[skillName] = []
        }
        questionsBySkill[skillName].push(q)
      })

      // Merge with knowledge summaries
      const groups = (prePracticeContent.knowledgeSummaries || []).map((summary) => ({
        skillName: summary.skillName,
        summary: summary.summary,
        detailedExplanation: summary.detailedExplanation,
        examples: summary.examples || [],
        commonMistakes: summary.commonMistakes || [],
        memoryTips: summary.memoryTips || [],
        concepts: summary.concepts || [],
        wrongQuestions: questionsBySkill[summary.skillName] || []
      }))

      // Add any skills that have wrong questions but no knowledge summary
      Object.entries(questionsBySkill).forEach(([skillName, questions]) => {
        if (!groups.find((g) => g.skillName === skillName)) {
          groups.push({
            skillName,
            summary: null,
            detailedExplanation: null,
            examples: [],
            commonMistakes: [],
            memoryTips: [],
            concepts: [],
            wrongQuestions: questions
          })
        }
      })

      setSkillGroups(groups)

      // Expand first skill by default
      if (groups.length > 0) {
        setExpandedSkills({ 0: true })
      }
    }
  }, [prePracticeContent])

  const toggleSkill = (index) => {
    setExpandedSkills((prev) => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const toggleQuestion = (questionId) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId]
    }))

    // Mark as reviewed when expanded
    if (!reviewedIds.includes(questionId)) {
      setReviewedIds((prev) => [...prev, questionId])
    }
  }

  const handleStartPractice = async () => {
    setIsStarting(true)
    try {
      // Complete pre-practice review (only for pending/pre_practice status)
      if (!practiceStatus.isStarted) {
        const reviewResult = await completePrePracticeReview(sessionId, reviewedIds)
        if (!reviewResult.success) {
          toast.error(getErrorMessage(reviewResult.error))
          setIsStarting(false)
          return
        }
      }

      // Start practice - creates a Challenge and redirects to Challenge session
      const startResult = await startPractice(sessionId)
      const homework = isHomework || startResult.data?.session?.revealPerQuestion
      if (startResult.success && homework) {
        // Tutor homework: dedicated per-question reveal page.
        navigate(`/focused-practice/${sessionId}/homework`)
      } else if (startResult.success && startResult.data?.challenge?.id) {
        // Redirect to the standard Challenge session UI
        navigate(`/challenge-session/${startResult.data.challenge.id}`)
      } else if (startResult.success) {
        // Fallback if challengeId not returned (shouldn't happen)
        toast.error('Failed to create practice challenge')
      } else {
        toast.error(getErrorMessage(startResult.error))
      }
    } catch (err) {
      toast.error('Failed to start practice')
    } finally {
      setIsStarting(false)
    }
  }

  // Continue an in-progress practice (homework → dedicated page; else ChallengeSession)
  const handleContinuePractice = () => {
    if (isHomework) {
      navigate(`/focused-practice/${sessionId}/homework`)
    } else if (practiceStatus.challengeId) {
      navigate(`/challenge-session/${practiceStatus.challengeId}`)
    }
  }

  // View results page
  const handleViewResults = () => {
    navigate(`/focused-practice/${sessionId}/results`)
  }

  // Review completed practice answers (read-only mode)
  const handleReviewAnswers = () => {
    navigate(`/focused-practice/${sessionId}/practice?mode=review`)
  }

  // Reset practice to start over
  const handleResetPractice = async () => {
    setIsResetting(true)
    try {
      const result = await resetSession(sessionId)
      if (result.success) {
        toast.success('Practice reset! Starting fresh...')
        // Homework → dedicated page; otherwise the standard ChallengeSession.
        const challengeId = result.data?.challengeId || practiceStatus.challengeId
        if (isHomework) {
          navigate(`/focused-practice/${sessionId}/homework`)
        } else if (challengeId) {
          navigate(`/challenge-session/${challengeId}`)
        } else {
          // Reload to refresh state
          loadContent()
        }
      } else {
        toast.error(getErrorMessage(result.error) || 'Failed to reset practice')
      }
    } catch (err) {
      toast.error('Failed to reset practice')
    } finally {
      setIsResetting(false)
    }
  }

  const getAnswerLabel = (answer) => {
    if (answer === null || answer === undefined) return '-'
    // Handle array answers (e.g., ["A"] from correct_answer)
    if (Array.isArray(answer)) {
      return answer.length > 0 ? getAnswerLabel(answer[0]) : '-'
    }
    // Convert to string if number or other type
    const answerStr = String(answer)
    // Handle letter answers (A, B, C, D)
    if (answerStr.length === 1 && /[A-D]/i.test(answerStr)) {
      return answerStr.toUpperCase()
    }
    return answerStr
  }

  if (loading && !prePracticeContent) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    )
  }

  if (error && !prePracticeContent) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600">{getErrorMessage(error)}</p>
          <button
            onClick={() => navigate('/focused-practice')}
            className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
          >
            Back to List
          </button>
        </div>
      </div>
    )
  }

  const session = prePracticeContent?.session || currentSession
  const knowledgeSummaries = prePracticeContent?.knowledgeSummaries || []
  const wrongQuestions = prePracticeContent?.wrongQuestions || []

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/focused-practice')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Target className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {session?.name || 'Pre-Practice Review'}
              </h1>
              <p className="text-sm text-gray-600">
                Review the material before starting your practice
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Session Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Target Domain</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {session?.targetDomain}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600 dark:text-gray-400">Source Accuracy</p>
            <p className="text-lg font-semibold text-amber-600">
              {session?.sourceAccuracy?.toFixed(0) || '-'}%
            </p>
          </div>
        </div>
      </div>

      {/* Skills Navigation Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <BookOpen className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Skills to Review
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {skillGroups.length} skill{skillGroups.length !== 1 ? 's' : ''} to master
              </p>
            </div>
          </div>

          {/* Skill Navigator Pills */}
          {skillGroups.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveSkillIndex((prev) => Math.max(0, prev - 1))}
                disabled={activeSkillIndex === 0}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
              <div className="flex gap-1.5">
                {skillGroups.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveSkillIndex(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      idx === activeSkillIndex
                        ? 'bg-primary-600 dark:bg-primary-400 scale-125'
                        : idx < activeSkillIndex
                          ? 'bg-green-500'
                          : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                    }`}
                    title={`Skill ${idx + 1}: ${skillGroups[idx]?.skillName}`}
                  />
                ))}
              </div>
              <button
                onClick={() =>
                  setActiveSkillIndex((prev) => Math.min(skillGroups.length - 1, prev + 1))
                }
                disabled={activeSkillIndex === skillGroups.length - 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                {activeSkillIndex + 1} of {skillGroups.length}
              </span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Review Progress</span>
            <span>
              {Math.round(
                (reviewedIds.length /
                  Math.max(
                    1,
                    skillGroups.reduce((sum, s) => sum + s.wrongQuestions.length, 0)
                  )) *
                  100
              )}
              %
            </span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-purple-500 rounded-full transition-all duration-300"
              style={{
                width: `${Math.round(
                  (reviewedIds.length /
                    Math.max(
                      1,
                      skillGroups.reduce((sum, s) => sum + s.wrongQuestions.length, 0)
                    )) *
                    100
                )}%`
              }}
            />
          </div>
        </div>
      </div>

      {/* Skill Content - Tabbed Interface */}
      {skillGroups.length > 0 ? (
        <div className="mb-6">
          <SkillContentTabs
            skill={skillGroups[activeSkillIndex]}
            expandedQuestions={expandedQuestions}
            toggleQuestion={toggleQuestion}
            reviewedIds={reviewedIds}
            getAnswerLabel={getAnswerLabel}
          />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 mb-6 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Great job!
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            No skills to review. You may proceed to practice.
          </p>
        </div>
      )}

      {/* Review Completion Status */}
      {(() => {
        const totalQuestions = skillGroups.reduce((sum, s) => sum + s.wrongQuestions.length, 0)
        const allReviewed = reviewedIds.length === totalQuestions && totalQuestions > 0
        return allReviewed ? (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full">
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-semibold text-green-800 dark:text-green-200">
                  All questions reviewed!
                </p>
                <p className="text-sm text-green-700 dark:text-green-300">
                  You've reviewed all {totalQuestions} questions. Ready to practice!
                </p>
              </div>
            </div>
          </div>
        ) : null
      })()}

      {/* Action Buttons - Different based on practice status */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        {/* Practice Status Banner */}
        {practiceStatus.isStarted && (
          <div
            className={`mb-4 p-4 rounded-lg border ${
              practiceStatus.isCompleted
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {practiceStatus.isCompleted ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="font-medium text-green-800 dark:text-green-200">
                      Practice Completed
                    </span>
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    <span className="font-medium text-primary-800 dark:text-primary-200">
                      Practice In Progress
                    </span>
                  </>
                )}
              </div>
              {practiceStatus.totalQuestions > 0 && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {practiceStatus.answeredCount} / {practiceStatus.totalQuestions} answered
                </span>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-4">
          {/* Not started - Show Start Practice button */}
          {!practiceStatus.isStarted && (
            <button
              onClick={handleStartPractice}
              disabled={isStarting}
              className="flex items-center gap-2 px-8 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {isStarting ? (
                <>
                  <LoadingSpinner size="sm" className="text-white" />
                  Starting Practice...
                </>
              ) : (
                <>
                  <PlayCircle className="h-5 w-5" />
                  Start Practice ({session?.questionCount || 10} Questions)
                </>
              )}
            </button>
          )}

          {/* In progress but not completed - Show Continue button */}
          {practiceStatus.isStarted && !practiceStatus.isCompleted && (
            <button
              onClick={handleContinuePractice}
              className="flex items-center gap-2 px-8 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors shadow-lg"
            >
              <PlayCircle className="h-5 w-5" />
              Continue Practice
            </button>
          )}

          {/* Completed - Show Review, Results, and Reset buttons */}
          {practiceStatus.isCompleted && (
            <>
              <button
                onClick={handleReviewAnswers}
                className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors shadow-lg"
              >
                <Eye className="h-5 w-5" />
                Review Answers
              </button>
              <button
                onClick={handleViewResults}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-lg"
              >
                <BarChart3 className="h-5 w-5" />
                View Results
              </button>
              <button
                onClick={handleResetPractice}
                disabled={isResetting}
                className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResetting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-5 w-5" />
                    Reset & Redo
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default FocusedPracticeReview
